import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { ToastContainer, toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Select from "react-select";
import colors from "assets/theme/base/colors";
import "react-toastify/dist/ReactToastify.css";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import "../campus/Campus.css";
import { hasAdmissionPermission } from "hooks/permissions";
const { info } = colors;
const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const emptyFormData = {
  code: "",
  name: "",
  duration: "",
  description: "",
  department_ids: [],
  degree_abbreviation: "",
  credit_hours: "",
  status: true,
  program_level: "",
  type: "",
  group: "",
  cost_center: "",
  cost_center_description: "",
};

const toDepartmentIds = (values = []) => [
  ...new Set(
    (values || [])
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value) && value > 0)
  ),
];

const Program = () => {
  // State variables for programs
  const [programs, setPrograms] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({ ...emptyFormData });
  
  const [showAddProgramModal, setShowAddProgramModal] = useState(false);
  const [showUpdateProgramModal, setShowUpdateProgramModal] = useState(false);
  const [showDeleteProgramModal, setShowDeleteProgramModal] = useState(false);
  const [currentProgramId, setCurrentProgramId] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [showViewProgramModal, setShowViewProgramModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  // Enums
  const programLevels = [
    "Undergraduate (BS Programs)",
    "Graduate (MS, M-Phil Programs)",
    "Doctorate (PhD Programs)",
    "5th Semester"
  ];
  const programTypes = ["ANNUAL", "NORMAL"];
  const programGroups = ["MORNING", "AFTERNOON", "EVENING", "WEEKEND"];

  const departmentOptions = departments.map((dept) => ({
    value: Number(dept.id),
    label: dept.name,
  }));

  const selectedDepartmentOptions = departmentOptions.filter((opt) =>
    formData.department_ids.includes(Number(opt.value))
  );

  // Fetch faculties
  const getPrograms = async() => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    await fetch(`${BASE_URL}/degree/get_degrees`, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        const degrees = data.degrees || [];
        setAllPrograms(degrees);
        setPrograms(degrees);
        setSearchKeyword("");
      })
      .catch((error) => console.log(error));
  };

  // Fetch departments for dropdown
  const getDepartments = async () => {
    try {
      const response = await fetch(`${BASE_URL}/department/get_departments`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setDepartments(data.departments);
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error("Failed to fetch departments");
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    if (!formData.code) errors.code = "Program code is required";
    if (!formData.name) errors.name = "Program name is required";
    if (!formData.duration) errors.duration = "Duration is required";
    if (!formData.department_ids || formData.department_ids.length === 0) {
      errors.department_ids = "At least one department is required";
    }
    if (!formData.degree_abbreviation) errors.degree_abbreviation = "Abbreviation is required";
    if (!formData.credit_hours) errors.credit_hours = "Credit hours is required";
    if (!formData.program_level) errors.program_level = "Program level is required";
    if (!formData.type) errors.type = "Program type is required";
    if (!formData.group) errors.group = "Program group is required";
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildProgramPayload = () => {
    const department_ids = toDepartmentIds(formData.department_ids);
    return {
      ...formData,
      department_ids,
      department_id: department_ids[0],
    };
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleDepartmentsChange = (selected) => {
    const department_ids = toDepartmentIds(
      selected ? selected.map((item) => item.value) : []
    );
    setFormData((prev) => ({
      ...prev,
      department_ids,
    }));
    if (formErrors.department_ids) {
      setFormErrors((prev) => ({ ...prev, department_ids: "" }));
    }
  };

  // Add Program
  const addProgram = async () => {
    if (!validateForm()) return;

    try {
      const response = await fetch(`${BASE_URL}/degree/add_program`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildProgramPayload()),
      });

      if (!response.ok) {
        throw new Error("Failed to add program");
      }

      await getPrograms(); // Refresh the programs list
      toast.success("Program added successfully");
      setShowAddProgramModal(false);
      resetForm();
    } catch (error) {
      console.error("Error adding program:", error);
      toast.error("Failed to add program");
    }
  };

  // Delete Program
  const deleteProgram = () => {
    const updatedPrograms = programs.filter((Program) => Program.id !== currentProgramId);
    setPrograms(updatedPrograms);
    setAllPrograms((prev) => prev.filter((program) => program.id !== currentProgramId));
    toast.success("Program deleted successfully");
    setShowDeleteProgramModal(false);
  };

  // Update Program
  const updateProgram = async () => {
    if (!validateForm()) return;

    try {
      const response = await fetch(`${BASE_URL}/degree/update_program`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...buildProgramPayload(), id: currentProgramId }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.error) {
        throw new Error(result.error || "Failed to update program");
      }

      await getPrograms(); // Refresh the programs list
      toast.success(result.success || "Program updated successfully");
      setShowUpdateProgramModal(false);
      resetForm();
    } catch (error) {
      console.error("Error updating program:", error);
      toast.error(error.message || "Failed to update program");
    }
  };

  const resetForm = () => {
    setFormData({ ...emptyFormData });
    setFormErrors({});
  };

  // Search functionality — always filter from full list
  const handleSearchKeywordChange = (e) => {
    const rawValue = e.target.value;
    setSearchKeyword(rawValue);
    const searchTerm = rawValue.trim().toLowerCase();

    if (!searchTerm) {
      setPrograms(allPrograms);
      return;
    }

    const filteredPrograms = allPrograms.filter((program) => {
      const nameMatch = (program.name || "").toLowerCase().includes(searchTerm);
      const departmentMatch = (program.department_name || "")
        .toLowerCase()
        .includes(searchTerm);
      const abbreviationMatch = (program.degree_abbreviation || "")
        .toLowerCase()
        .includes(searchTerm);
      const programLevelMatch = (program.program_level || "")
        .toLowerCase()
        .includes(searchTerm);
      const programCodeMatch = (program.code || "").toLowerCase().includes(searchTerm);

      return (
        nameMatch ||
        departmentMatch ||
        abbreviationMatch ||
        programLevelMatch ||
        programCodeMatch
      );
    });

    setPrograms(filteredPrograms);
  };
  
  // Dialog handlers
  const showProgramAddModal = () => setShowAddProgramModal(true);
  const showProgramUpdateModal = (id) => {
    setCurrentProgramId(id);
    const program = allPrograms.find((p) => p.id === id) || programs.find((p) => p.id === id);
    const department_ids = toDepartmentIds(
      program.department_ids && program.department_ids.length > 0
        ? program.department_ids
        : program.department_id
        ? [program.department_id]
        : []
    );
    setFormData({
      code: program.code,
      name: program.name,
      duration: program.duration,
      description: program.description,
      department_ids,
      degree_abbreviation: program.degree_abbreviation,
      credit_hours: program.credit_hours,
      status: program.status,
      program_level: program.program_level,
      type: program.type,
      group: program.group,
      cost_center: program.cost_center,
      cost_center_description: program.cost_center_description
    });
    setShowUpdateProgramModal(true);
  };

  const showProgramDeleteModal = (id) => {
    setCurrentProgramId(id);
    setShowDeleteProgramModal(true);
  };

  const viewProgram = (id) => {
    const Program = allPrograms.find((f) => f.id === id) || programs.find((f) => f.id === id);
    setSelectedProgram(Program);
    setShowViewProgramModal(true);
  };

  // Initial render
  useEffect(() => {
    getPrograms();
    getDepartments();
  }, []);

  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Program Name", accessor: "program_name", align: "center" },
    { Header: "Program Code", accessor: "code", align: "center" },
    { Header: "Department", accessor: "department_name", align: "center" },
    { Header: "Group", accessor: "group", align: "center" },
    { Header: "Abbreviation", accessor: "degree_abbreviation", align: "center" },
    { Header: "Duration (Years)", accessor: "duration", align: "center" },
    { Header: "Credit Hours", accessor: "credit_hours", align: "center" },
    { Header: "Program Level", accessor: "program_level", align: "center" },
    // Uncomment this if you want to include actions
    { Header: "Actions", accessor: "actions", align: "center" },
  ];
  
  const rows = programs.map((degree, index) => ({
    serial: index + 1,
    program_name: degree.name,
    code: degree.code,
    department_name: degree.department_name,
    group: degree.group,
    degree_abbreviation: degree.degree_abbreviation,
    duration: degree.duration,
    credit_hours: degree.credit_hours,
    program_level: degree.program_level,
    // Uncomment and customize if you want to include actions
    actions: (
      <>
      {hasAdmissionPermission("Programs", "UPDATE") && (
        <IconButton onClick={() => showProgramUpdateModal(degree.id)}>
          <EditIcon style={{ color: "#66CDAA" }} />
        </IconButton>
      )}
      {hasAdmissionPermission("Programs", "READ") && (
        <IconButton onClick={() => viewProgram(degree.id)}>
          <VisibilityOutlinedIcon style={{ color: "#45B3FA" }} />
        </IconButton>
      )}
        {hasAdmissionPermission("Programs", "DELETE") && (
        <IconButton onClick={() => showProgramDeleteModal(degree.id)}>
          <DeleteIcon style={{ color: "#FF6F30" }} />
        </IconButton>
        )}
      </>
    ),
  }));

  const departmentSelectField = (
    <div className="form-outline mt-3">
      <label>Departments</label>
      <Select
        isMulti
        name="department_ids"
        options={departmentOptions}
        className="basic-multi-select"
        classNamePrefix="select"
        placeholder="Select Departments"
        value={selectedDepartmentOptions}
        onChange={handleDepartmentsChange}
      />
      {formErrors.department_ids && (
        <div className="error-message1">{formErrors.department_ids}</div>
      )}
    </div>
  );
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card
              className="card1"
            >
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Paper
                  component="form"
                 className="search-paper"
                 onSubmit={(e) => e.preventDefault()}
                >
                  <InputBase
                    sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                    placeholder="Search"
                    inputProps={{ "aria-label": "search Program" }}
                    value={searchKeyword}
                    onChange={handleSearchKeywordChange}
                  />
                  <SearchIcon
                   className="search-icon"
                  />
                </Paper>
                { hasAdmissionPermission("Programs", "CREATE") && (
                <Button
                  variant="filled"
                  endIcon={
                    <AddIcon
                     className="add-icon"
                    />
                  }
                  onClick={showProgramAddModal}
                   className="add-button"
                >
                  Add
                </Button>
                )}
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Program Dialog */}
      <Dialog
        open={showAddProgramModal}
        onClose={() => setShowAddProgramModal(false)}
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          Add Program
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6 left-fields">
              <div className="form-outline mt-3">
                <label>Program Code</label>
                <input
                  name="code"
                  type="text"
                  value={formData.code}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Program Code"
                />
                {formErrors.code && <div className="error-message1">{formErrors.code}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Name</label>
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Program Name"
                />
                {formErrors.name && <div className="error-message1">{formErrors.name}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Duration (Years)</label>
                <input
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Duration"
                />
                {formErrors.duration && <div className="error-message1">{formErrors.duration}</div>}
              </div>

              {departmentSelectField}

              <div className="form-outline mt-3">
                <label>Degree Abbreviation</label>
                <input
                  name="degree_abbreviation"
                  type="text"
                  value={formData.degree_abbreviation}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Abbreviation"
                />
                {formErrors.degree_abbreviation && <div className="error-message1">{formErrors.degree_abbreviation}</div>}
              </div>
            </div>

            <div className="col-md-6 form-sideline">
              <div className="form-outline mt-3">
                <label>Credit Hours</label>
                <input
                  name="credit_hours"
                  type="number"
                  value={formData.credit_hours}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Credit Hours"
                />
                {formErrors.credit_hours && <div className="error-message">{formErrors.credit_hours}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Level</label>
                <select
                  name="program_level"
                  value={formData.program_level}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                >
                  <option value="">Select Program Level</option>
                  {programLevels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
                {formErrors.program_level && <div className="error-message">{formErrors.program_level}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                >
                  <option value="">Select Program Type</option>
                  {programTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {formErrors.type && <div className="error-message">{formErrors.type}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Group</label>
                <select
                  name="group"
                  value={formData.group}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                >
                  <option value="">Select Program Group</option>
                  {programGroups.map((group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </select>
                {formErrors.group && <div className="error-message">{formErrors.group}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Cost Center</label>
                <input
                  name="cost_center"
                  type="text"
                  value={formData.cost_center}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Cost Center"
                />
              </div>
            </div>

            <div className="col-12 mt-3">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-control form-control-lg"
                rows={3}
                placeholder="Enter Description"
              />
            </div>

            <div className="col-12 mt-3">
              <label>Cost Center Description</label>
              <textarea
                name="cost_center_description"
                value={formData.cost_center_description}
                onChange={handleInputChange}
                className="form-control form-control-lg"
                rows={2}
                placeholder="Enter Cost Center Description"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddProgramModal(false);
              resetForm();
            }}
            className="dialog-actions-button negative"
          >
            Cancel
          </Button>
          <Button
            onClick={addProgram}
            className="dialog-actions-button positive"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Program Dialog */}
      <Dialog
        open={showUpdateProgramModal}
        onClose={() => setShowUpdateProgramModal(false)}
        maxWidth="lg"
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-title">
          Update Program
        </DialogTitle>
        <DialogContent className="dialog-content">
          <div className="row">
            <div className="col-md-6 left-fields">
              <div className="form-outline mt-3">
                <label>Program Code</label>
                <input
                  name="code"
                  type="text"
                  value={formData.code}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Program Code"
                />
                {formErrors.code && <div className="error-message1">{formErrors.code}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Name</label>
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Program Name"
                />
                {formErrors.name && <div className="error-message1">{formErrors.name}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Duration (Years)</label>
                <input
                  name="duration"
                  type="number"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Duration"
                />
                {formErrors.duration && <div className="error-message1">{formErrors.duration}</div>}
              </div>

              {departmentSelectField}

              <div className="form-outline mt-3">
                <label>Degree Abbreviation</label>
                <input
                  name="degree_abbreviation"
                  type="text"
                  value={formData.degree_abbreviation}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Abbreviation"
                />
                {formErrors.degree_abbreviation && <div className="error-message1">{formErrors.degree_abbreviation}</div>}
              </div>
            </div>

            <div className="col-md-6 form-sideline">
              <div className="form-outline mt-3">
                <label>Credit Hours</label>
                <input
                  name="credit_hours"
                  type="number"
                  value={formData.credit_hours}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Credit Hours"
                />
                {formErrors.credit_hours && <div className="error-message">{formErrors.credit_hours}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Level</label>
                <select
                  name="program_level"
                  value={formData.program_level}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                >
                  <option value="">Select Program Level</option>
                  {programLevels.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
                {formErrors.program_level && <div className="error-message">{formErrors.program_level}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                >
                  <option value="">Select Program Type</option>
                  {programTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {formErrors.type && <div className="error-message">{formErrors.type}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Program Group</label>
                <select
                  name="group"
                  value={formData.group}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                >
                  <option value="">Select Program Group</option>
                  {programGroups.map((group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </select>
                {formErrors.group && <div className="error-message">{formErrors.group}</div>}
              </div>

              <div className="form-outline mt-3">
                <label>Cost Center</label>
                <input
                  name="cost_center"
                  type="text"
                  value={formData.cost_center}
                  onChange={handleInputChange}
                  className="form-control form-control-lg"
                  placeholder="Enter Cost Center"
                />
              </div>
            </div>

            <div className="col-12 mt-3">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-control form-control-lg"
                rows={3}
                placeholder="Enter Description"
              />
            </div>

            <div className="col-12 mt-3">
              <label>Cost Center Description</label>
              <textarea
                name="cost_center_description"
                value={formData.cost_center_description}
                onChange={handleInputChange}
                className="form-control form-control-lg"
                rows={2}
                placeholder="Enter Cost Center Description"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUpdateProgramModal(false);
              resetForm();
            }}
            className="dialog-actions-button negative"
          >
            Cancel
          </Button>
          <Button
            onClick={updateProgram}
            className="dialog-actions-button positive"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Program Dialog */}
      <Dialog
        open={showDeleteProgramModal}
        onClose={() => setShowDeleteProgramModal(false)}
        PaperProps={{
          sx: {
            width: "450px",
            borderRadius: "20px",
            opacity: 1,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle className="dialog-delete">Delete Program</DialogTitle>
        <DialogContent className="content-delete">
          Are you sure you want to delete this program?
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowDeleteProgramModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-actions-button positive"
            onClick={deleteProgram}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Program Dialog */}
      <Dialog
        open={showViewProgramModal}
        onClose={() => setShowViewProgramModal(false)}
        PaperProps={{
          className: "dialog-paper",
        }}
      >
        <DialogTitle id="responsive-dialog-title" className="dialog-delete">
          View Program
        </DialogTitle>
        <DialogContent className="dialog-content">
          {selectedProgram && (
            <>
              <div className="flex-container">
                <strong className="label1">Program Code</strong>
                <span className="value1">{selectedProgram.code}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Program Name</strong>
                <span className="value1">{selectedProgram.name}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Departments</strong>
                <span className="value1">{selectedProgram.department_name}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Duration</strong>
                <span className="value1">{selectedProgram.duration} Years</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Credit Hours</strong>
                <span className="value1">{selectedProgram.credit_hours}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Program Level</strong>
                <span className="value1">{selectedProgram.program_level}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Program Type</strong>
                <span className="value1">{selectedProgram.type}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Program Group</strong>
                <span className="value1">{selectedProgram.group}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Description</strong>
                <span className="value1">{selectedProgram.description}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Cost Center</strong>
                <span className="value1">{selectedProgram.cost_center}</span>
              </div>
              <div className="flex-container">
                <strong className="label1">Cost Center Description</strong>
                <span className="value1">{selectedProgram.cost_center_description}</span>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-actions-button negative"
            onClick={() => setShowViewProgramModal(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Program;